import React from "react";
import { format } from "date-fns";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import GetAppIcon from "@material-ui/icons/GetApp";
import { DATE_YEAR_MONTH_DAY_TIME_FORMAT } from "constants/date-fns/dateTime.constant";
import { getDownloadTokenExpiry } from "../../utils/ManageRecordings.util";
import { column_keys } from "../../constants/ManageRecordings.constant";
import { copyToClipboard } from "features/Common/utils/Copy.utils";
import { useNotifications } from "utils/hooks/useNotifications";
import { is_empty } from "features/Common/utils/common.utils";
import { validURLV2 } from "utils/validations";

export const ManageRecordingsDateFormatter = ({ record, fieldKey }) => {
  const dateVal = record?.[fieldKey] || record?.[column_keys[fieldKey]?.apiKey];
  if (is_empty(dateVal)) return "-";
  try {
    return format(new Date(dateVal), DATE_YEAR_MONTH_DAY_TIME_FORMAT);
  } catch (e) {
    return String(dateVal);
  }
};

export const ManageRecordingsExpiryFormatter = ({ record }) => {
  const { expiryDate, isExpired } = getDownloadTokenExpiry(record);
  if (isExpired) {
    return (
      <span className="tw-px-2 tw-py-0.5 tw-bg-red-100 tw-text-red-700 tw-text-xs tw-font-semibold tw-rounded-full tw-inline-block">
        Expired
      </span>
    );
  }
  if (expiryDate) {
    try {
      return format(expiryDate, DATE_YEAR_MONTH_DAY_TIME_FORMAT);
    } catch (e) {
      return String(expiryDate);
    }
  }
  return "-";
};

export const ManageRecordingsPasswordFormatter = ({ record, onCopy }) => {
  const { notify } = useNotifications();
  const password = record?.password;
  if (is_empty(password)) return "-";

  const handleCopy = (e) => {
    if (e?.stopPropagation) {
      e.stopPropagation();
    }
    if (onCopy) {
      onCopy(password);
    } else {
      copyToClipboard(password, notify);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleCopy}
      title="Click to copy password"
      className="tw-flex tw-items-center tw-gap-1.5 tw-p-1 tw-border-dotted tw-rounded-md tw-border-gray-400 tw-cursor-pointer hover:tw-bg-gray-50"
    >
      <span className="tw-pl-1 tw-font-mono tw-text-sm">{password}</span>
      <button
        type="button"
        title="Copy password"
        onClick={handleCopy}
        className="tw-p-1 tw-text-gray-500 hover:tw-text-blue-600 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-rounded hover:tw-bg-gray-100 tw-transition-colors"
      >
        <FileCopyIcon style={{ fontSize: 16 }} />
      </button>
    </div>
  );
};

export const ManageRecordingsActionFormatter = ({ record, onCopy }) => {
  const { notify } = useNotifications();
  const shareUrl = record?.share_url || record?.download_url;
  const password = record?.password;

  const handleCopyLink = async (e) => {
    if (e?.stopPropagation) {
      e.stopPropagation();
    }
    const urlToCopy = record?.download_url || record?.share_url;
    if (!is_empty(urlToCopy)) {
      if (onCopy) {
        await onCopy(urlToCopy);
      } else {
        await copyToClipboard(urlToCopy, notify);
      }
    }
  };

  const handleDownloadClick = async (e) => {
    if (e?.stopPropagation) {
      e.stopPropagation();
    }

    if (!is_empty(password)) {
      if (onCopy) {
        await onCopy(password);
      } else {
        await copyToClipboard(password, notify);
      }
    }

    if (!is_empty(shareUrl) && validURLV2(shareUrl)) {
      window.open(shareUrl, "_blank", "noopener,noreferrer");
    }
  };

  if (is_empty(shareUrl)) return "-";

  return (
    <div className="tw-flex tw-items-center tw-gap-1">
      <button
        type="button"
        title="Copy download link"
        className="tw-p-1.5 tw-text-gray-600 hover:tw-text-blue-600 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-rounded hover:tw-bg-gray-100 tw-transition-colors tw-flex tw-items-center tw-justify-center"
        onClick={handleCopyLink}
      >
        <FileCopyIcon style={{ fontSize: 18 }} />
      </button>
      <div className="tw-h-4 tw-w-px tw-bg-gray-300"></div>
      <button
        type="button"
        title="Download recording"
        className="tw-p-1.5 tw-text-gray-600 hover:tw-text-blue-600 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-rounded hover:tw-bg-gray-100 tw-transition-colors tw-flex tw-items-center tw-justify-center"
        onClick={handleDownloadClick}
      >
        <GetAppIcon style={{ fontSize: 18 }} />
      </button>
    </div>
  );
};
